import React, { useEffect, useMemo, useRef, useState } from "react";
import { FormikProps } from "formik";
import Modal from "react-bootstrap/Modal";
import { Button, Col, Form, Row } from "react-bootstrap";
import i18next, { TFunction } from "i18next";

import TranslationMissingDot from "../../Me-menu/components/LanguageVersionSelect/TranslationMissingDot";
import { Spinner } from "@services/ui-components";
import { CategoryPickerComponent } from "@me-pos/category-picker";

import { CloneDishFormValues } from "../hooks/useCloneDishModalBaseData";
import { useCloneDishModalFormData } from "../hooks/useCloneDishModalFormData";
import { cloneNameToDisplay } from "../../Me-menu/utils/cloneNameUtils";

type CloneDishModalFormProps = {
    isOpen: boolean;
    onClose: () => void;
    formik: FormikProps<CloneDishFormValues>;
    translate: TFunction;
    loading: boolean;
    error: unknown;
    isTranslationMode: boolean;
    translationTooltip: string;
    dishNameVm: {
        displayText?: string | null;
        isMissingTranslation?: boolean;
    };
    categories: any[] | null;
    collapseSpaces: (value: string) => string;
    normalizeDishName: (value: string) => string;
    primaryLanguage: string | null;
    canCloneDish: boolean;
};

const CloneDishModalForm: React.FC<CloneDishModalFormProps> = ({
    isOpen,
    onClose,
    formik,
    translate,
    loading,
    error,
    isTranslationMode,
    translationTooltip,
    dishNameVm,
    categories,
    collapseSpaces,
    normalizeDishName,
    primaryLanguage,
    canCloneDish,
}) => {
    const [isCategoryPickerVisible, setIsCategoryPickerVisible] = useState(false);
    const categoryTriggerRef = useRef<HTMLDivElement | null>(null);
    const categoryPickerRef = useRef<HTMLDivElement | null>(null);

    const { values, errors, touched, handleBlur, handleSubmit, setFieldValue, setFieldTouched, isSubmitting } = formik;

    const { serviceCategories } = useCloneDishModalFormData({
        categories,
        currentCategoryId: values.currentCategoryId,
        currentSubcategoryId: values.currentSubcategoryId,
        isTranslationMode,
    });

    useEffect(() => {
        if (!isCategoryPickerVisible) {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;

            const isClickInsideTrigger = categoryTriggerRef.current?.contains(target) ?? false;

            const isClickInsidePicker = categoryPickerRef.current?.contains(target) ?? false;

            if (!isClickInsideTrigger && !isClickInsidePicker) {
                setIsCategoryPickerVisible(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isCategoryPickerVisible]);

    const selectedCategoryLabel = useMemo(() => {
        if (!serviceCategories || !values.currentCategoryId) {
            return "";
        }

        const selectedCategory = serviceCategories.find((category: any) => category.id === values.currentCategoryId);

        if (!selectedCategory) {
            return "";
        }

        if (values.currentSubcategoryId && selectedCategory.subcategoryList?.length) {
            const selectedSubcategory = selectedCategory.subcategoryList.find(
                (subcategory: any) => subcategory.id === values.currentSubcategoryId
            );

            if (selectedSubcategory) {
                return selectedSubcategory.name;
            }
        }

        return selectedCategory.name;
    }, [serviceCategories, values.currentCategoryId, values.currentSubcategoryId]);

    const chevronIconClassName = isCategoryPickerVisible ? "bi-chevron-up" : "bi-chevron-down";

    return (
        <Modal
            show={isOpen}
            onHide={onClose}
            size="lg"
            centered
            animation
            aria-labelledby="clone-dish-modal"
            dialogClassName="clone-dish-modal"
        >
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton className="border-bottom-0 align-items-center">
                    <Modal.Title id="clone-dish-modal" className="mb-0 me-3">
                        <h5 className="mb-0">
                            <span className="me-1">
                                {translate("Cloning")}{" "}
                                {dishNameVm.displayText
                                    ? `"${cloneNameToDisplay(dishNameVm.displayText, translate)}"`
                                    : ""}
                            </span>

                            {dishNameVm.isMissingTranslation ? (
                                <TranslationMissingDot tooltipText={translationTooltip} />
                            ) : null}
                        </h5>
                    </Modal.Title>

                    <div className="rounded bg-additional-violet px-2 py-1 d-flex align-items-center">
                        <span className="text-primary px-1 fs-7 line-height-140">{primaryLanguage}</span>
                    </div>
                </Modal.Header>

                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Row className="align-items-center">
                            <Form.Label column sm={3} className="mb-0 fw-bold">
                                {translate("Dish name")} <span className="text-danger">*</span>
                            </Form.Label>

                            <Col sm={9}>
                                <Form.Control
                                    type="text"
                                    name="dishName"
                                    value={values.dishName}
                                    maxLength={50}
                                    isInvalid={Boolean(touched.dishName && errors.dishName)}
                                    onBlur={(e) => {
                                        setFieldValue("dishName", normalizeDishName(e.target.value));
                                        handleBlur(e);
                                    }}
                                    onChange={(e) => {
                                        setFieldValue("dishName", collapseSpaces(e.target.value));
                                    }}
                                />
                            </Col>
                        </Row>

                        <Row>
                            <Col sm={{ span: 9, offset: 3 }}>
                                <div className="text-grey fs-7 mt-1">{values.dishName.length}/50</div>

                                {touched.dishName && errors.dishName ? (
                                    <div className="text-danger fs-7 mt-1">{errors.dishName}</div>
                                ) : null}
                            </Col>
                        </Row>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Row className="align-items-center">
                            <Form.Label column sm={3} className="mb-0 fw-bold">
                                {translate("Category")} <span className="text-danger">*</span>
                            </Form.Label>

                            <Col sm={9}>
                                {loading ? (
                                    <div className="d-flex py-2">
                                        <Spinner heightContent={true} />
                                    </div>
                                ) : error ? (
                                    <div className="text-danger">{translate("Something went wrong")}</div>
                                ) : (
                                    <div
                                        ref={categoryTriggerRef}
                                        className={`form-control d-flex align-items-center justify-content-between ${
                                            touched.currentCategoryId && errors.currentCategoryId ? "is-invalid" : ""
                                        }`}
                                        role="button"
                                        tabIndex={0}
                                        style={{ cursor: "pointer" }}
                                        onClick={() => setIsCategoryPickerVisible((prev) => !prev)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                setIsCategoryPickerVisible((prev) => !prev);
                                            }

                                            if (e.key === "Escape") {
                                                setIsCategoryPickerVisible(false);
                                            }
                                        }}
                                    >
                                        <span className={selectedCategoryLabel ? "" : "text-grey"}>
                                            {selectedCategoryLabel || translate("Select category")}
                                        </span>

                                        <i className={`bi ${chevronIconClassName} me-2`} />
                                    </div>
                                )}
                            </Col>
                        </Row>

                        <Row>
                            <Col sm={{ span: 9, offset: 3 }}>
                                {touched.currentCategoryId && errors.currentCategoryId ? (
                                    <div className="text-danger fs-7 mt-1">{errors.currentCategoryId}</div>
                                ) : null}
                            </Col>
                        </Row>

                        {!loading && !error && isCategoryPickerVisible && serviceCategories ? (
                            <Row>
                                <Col sm={{ span: 9, offset: 3 }}>
                                    <div className={"position-relative"}>
                                        <div
                                            ref={categoryPickerRef}
                                            className="clone-dish-category-dropdown dropdown position-absolute p-1 opacity-1 z-2 top-10  start-0 end-0 mt-1 bg-white shadow"
                                        >
                                            <CategoryPickerComponent
                                                i18n={i18next}
                                                serviceCategories={serviceCategories}
                                                showMissingTranslationDot={isTranslationMode}
                                                renderMissingTranslationDot={() => (
                                                    <TranslationMissingDot tooltipText={translationTooltip} />
                                                )}
                                                onSearchChange={() => {}}
                                                onChangeCategory={(currentCategory) => {
                                                    if (currentCategory.isPicked) {
                                                        setFieldValue("currentCategoryId", currentCategory.id);
                                                        setFieldValue("currentSubcategoryId", null);
                                                        setFieldTouched("currentCategoryId", true, false);
                                                        setIsCategoryPickerVisible(false);
                                                    }
                                                }}
                                                onChangeSubcategory={(pickedSubcategory, currentCategory) => {
                                                    if (pickedSubcategory.isPicked) {
                                                        setFieldValue("currentCategoryId", currentCategory.id);
                                                        setFieldValue("currentSubcategoryId", pickedSubcategory.id);
                                                        setFieldTouched("currentCategoryId", true, false);
                                                        setIsCategoryPickerVisible(false);
                                                    }
                                                }}
                                                isOpen
                                            />
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        ) : null}
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer className="border-top-0">
                    <Row className="justify-content-between w-100">
                        <Col xs="3" className={"ps-0"}>
                            <Button
                                variant="outline-primary"
                                onClick={onClose}
                                disabled={isSubmitting}
                                className={"w-100"}
                            >
                                {translate("Cancel")}
                            </Button>
                        </Col>

                        <Col xs="3" className={"pe-0"}>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={isSubmitting || !canCloneDish}
                                className={"w-100"}
                            >
                                {translate("Clone")}
                            </Button>
                        </Col>
                    </Row>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default CloneDishModalForm;
